import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pushToast } from '../../lib/events';
import { overlayTokenQuery, rotateOverlayToken } from '../../lib/rooms';

interface HostRailOverlayProps {
  roomId: number;
}

/** Appends `only=reveal` to an overlay URL — WP6's second, dedicated OBS source that shows nothing
 * but the reveal card, layered over the game capture instead of the full scoreboard strip. */
function revealOnlyUrl(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}only=reveal`;
}

async function copyToClipboard(url: string, onSuccess: () => void, onError: () => void) {
  try {
    await navigator.clipboard.writeText(new URL(url, window.location.origin).toString());
    onSuccess();
  } catch {
    onError();
  }
}

/** The OBS overlay URL (the overlay page itself is WP6) — copy + rotate, the reveal-only variant,
 * and a short reminder of how the source is configured in OBS. */
export function HostRailOverlay({ roomId }: HostRailOverlayProps) {
  const queryClient = useQueryClient();
  const { data: overlay } = useQuery(overlayTokenQuery(roomId));

  const rotateMutation = useMutation({
    mutationFn: rotateOverlayToken,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: overlayTokenQuery(roomId).queryKey }),
    onError: () => pushToast('Could not rotate the overlay token.'),
  });

  function copyUrl(url: string) {
    void copyToClipboard(
      url,
      () => pushToast('Overlay URL copied.', 'success'),
      () => pushToast('Could not copy the URL — copy it from the address shown.'),
    );
  }

  return (
    <section>
      <div className="flex items-baseline gap-4">
        <span className="eyebrow">Overlay</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>
      <p className="telemetry mt-3 truncate text-text-muted" data-testid="overlay-url">{overlay?.url ?? 'Loading…'}</p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => overlay && copyUrl(overlay.url)}
          disabled={!overlay}
          className="min-h-tap flex-1 rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted hover:border-accent-border hover:text-text disabled:opacity-50"
        >
          Copy
        </button>
        <button
          type="button"
          disabled={rotateMutation.isPending}
          onClick={() => rotateMutation.mutate({ roomId })}
          className="min-h-tap flex-1 rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted hover:border-signal hover:text-signal disabled:opacity-50"
        >
          Rotate
        </button>
      </div>

      {overlay && (
        <>
          <button
            type="button"
            onClick={() => copyUrl(revealOnlyUrl(overlay.url))}
            className="mt-2 min-h-tap w-full rounded-md border border-border-strong px-3 text-left text-sm text-text-muted hover:border-accent-border hover:text-text"
          >
            Copy reveal-only source
          </button>
          <p className="telemetry mt-1 truncate text-text-faint" data-testid="overlay-reveal-url">
            {revealOnlyUrl(overlay.url)}
          </p>
        </>
      )}

      <p className="telemetry mt-3 text-text-faint">
        OBS: add a Browser Source, 1920×1080, this URL, &quot;Shutdown source when not visible&quot; off.
        The background is transparent — no custom CSS needed. Rotating closes any connected overlay
        and the old URL stops working; paste the new URL into OBS afterwards.
      </p>
    </section>
  );
}
